import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { axiosInstance } from '@/App';
import { useFM } from '@/context/FMContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ESCALATION_CATEGORIES = [
  'Compliance',
  'Discipline',
  'Revenue',
  'Member',
  'Equipment',
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/**
 * EscalationFAB - Floating Action Button for quick escalation entry.
 * Visible on every FM screen. Opens a dialog with category/severity/description form.
 */
export default function EscalationFAB() {
  const { selectedGymId } = useFM();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !severity || !description.trim()) {
      toast.error('Please fill all fields');
      return;
    }
    if (!selectedGymId) {
      toast.error('Please select a gym first');
      return;
    }

    try {
      setSubmitting(true);
      await axiosInstance.post('/fm/escalations', {
        gym_id: selectedGymId,
        category,
        severity,
        description: description.trim(),
      });
      toast.success('Escalation submitted');
      setOpen(false);
      setCategory('');
      setSeverity('');
      setDescription('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit escalation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-4 z-50 bg-red-500 hover:bg-red-600 text-white rounded-full w-14 h-14 shadow-lg flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Report Escalation"
      >
        <AlertTriangle className="w-6 h-6" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>Report Escalation</DialogTitle>
            <DialogDescription>Quickly log an issue for review</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {ESCALATION_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Severity</label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue..."
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm min-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                rows={3}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-md font-medium disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Escalation'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
